import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  getUsers,
  addUser,
  updateUser,
  deleteUser,
  getGoogleSheetStatus,
  refreshGoogleSheetToken,
} from "../services/UserServices";
import useAuthStore from "../store/useAuthStore";
import { useLocation, useNavigate } from "react-router-dom";

const initialUserForm = { name: "", email: "", role: "", pin: "" };

const Users = () => {
  const token = useAuthStore((state) => state.token);
  const isInternal = useAuthStore((state) => state.isInternal);

  const [userList, setUserList] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [addingUser, setAddingUser] = useState(false);
  const [formData, setFormData] = useState(initialUserForm);

  const [googleStatus, setGoogleStatus] = useState({
    loading: true,
    data: null,
    error: "",
  });
  const [showCreateSheetModal, setShowCreateSheetModal] = useState(false);
  const [newSheetName, setNewSheetName] = useState("");
  const [connectingSheet, setConnectingSheet] = useState(false);
  const [refreshingToken, setRefreshingToken] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const handledQueryRef = useRef("");

  const connectedSheetUrl = useMemo(() => {
    const sheetId = googleStatus?.data?.sheetId;
    return sheetId ? `https://docs.google.com/spreadsheets/d/${sheetId}` : "";
  }, [googleStatus?.data?.sheetId]);

  const getUsersList = async () => {
    try {
      const res = await getUsers();
      setUserList(res?.data?.data || []);
    } catch {
      toast.error("Failed to fetch users");
    }
  };

  const loadGoogleStatus = async ({ silent = false } = {}) => {
    if (!silent) {
      setGoogleStatus((prev) => ({ ...prev, loading: true, error: "" }));
    }

    try {
      const res = await getGoogleSheetStatus();
      setGoogleStatus({
        loading: false,
        data: res?.data || null,
        error: "",
      });
    } catch (error) {
      const message =
        error?.response?.data?.message || "Failed to fetch Google Sheet status";
      setGoogleStatus({ loading: false, data: null, error: message });
      if (!silent) toast.error(message);
    }
  };

  useEffect(() => {
    if (!token) {
      navigate("/");
      return;
    }

    getUsersList();
    loadGoogleStatus({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!location.search) return;
    if (handledQueryRef.current === location.search) return;
    handledQueryRef.current = location.search;

    const params = new URLSearchParams(location.search);
    const googleFlag = params.get("google");
    const reason = params.get("reason");

    if (googleFlag === "connected") {
      toast.success("Google Sheet connected successfully");
      loadGoogleStatus({ silent: true });
      window.history.replaceState({}, "", "/users");
    } else if (googleFlag === "error") {
      toast.error(reason || "Google connection failed");
      window.history.replaceState({}, "", "/users");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        await deleteUser(id);
        setUserList(userList.filter((u) => u.id !== id));
        toast.success("User deleted successfully");
      } catch {
        toast.error("Failed to delete user");
      }
    }
  };

  const handleEditClick = (user) => {
    setEditingUser(user.id);
    setFormData({
      name: user.name,
      email: user.email,
      role: user.role,
      pin: user.pin || "",
    });
  };

  const handleSave = async () => {
    if (!formData.name || !formData.email || !formData.role || !formData.pin) {
      toast.error("All fields are required");
      return;
    }

    try {
      await updateUser({ id: editingUser, ...formData });
      const updatedList = userList.map((u) =>
        u.id === editingUser ? { ...u, ...formData } : u
      );
      setUserList(updatedList);
      setEditingUser(null);
      toast.success("User updated successfully");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to update user");
    }
  };

  const handleAddUser = async () => {
    if (!formData.name || !formData.email || !formData.role || !formData.pin) {
      toast.error("All fields are required");
      return;
    }

    try {
      await addUser(formData);
      setAddingUser(false);
      setFormData(initialUserForm);
      toast.success("User added successfully");
      getUsersList();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to add user");
    }
  };

  const getConnectBaseUrl = () => {
    const configuredBase = isInternal
      ? process.env.REACT_APP_BACKEND_URL
      : process.env.REACT_APP_CLIENT_URL;

    const baseUrl = configuredBase || "http://localhost:4069";
    return `${baseUrl.replace(/\/$/, "")}/api/auth/auth`;
  };

  const redirectToGoogleConnect = ({ createNew, sheetName = "" }) => {
    if (!token) {
      toast.error("Session expired. Please login again.");
      return;
    }

    const trimmedSheetName = sheetName.trim();

    if (createNew && !trimmedSheetName) {
      toast.error("Sheet name is required");
      return;
    }

    setConnectingSheet(true);

    const queryParams = new URLSearchParams({
      token,
      createNew: createNew ? "true" : "false",
      redirectUri: `${window.location.origin}/users`,
    });

    if (createNew) {
      queryParams.set("sheetName", trimmedSheetName);
    }

    window.location.href = `${getConnectBaseUrl()}?${queryParams.toString()}`;
  };

  const handleCreateSheetSubmit = () => {
    redirectToGoogleConnect({ createNew: true, sheetName: newSheetName });
  };

  const handleRefreshGoogleStatus = async () => {
    await loadGoogleStatus();
    toast.success("Google Sheet status refreshed");
  };

  const handleRefreshToken = async () => {
    try {
      setRefreshingToken(true);
      await refreshGoogleSheetToken();
      toast.success("Google token refreshed");
      await loadGoogleStatus({ silent: true });
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          "Token refresh failed. Please reconnect Google."
      );
      await loadGoogleStatus({ silent: true });
    } finally {
      setRefreshingToken(false);
    }
  };

  const statusBadgeClass =
    googleStatus?.data?.tokenStatus === "valid"
      ? "bg-green-100 text-green-700"
      : "bg-red-100 text-red-700";

  return (
    <div className="w-full p-6">
      <h1 className="text-2xl w-10/12 mx-auto font-bold mb-4 text-center text-gray-800">
        Users
      </h1>

      <div className="w-10/12 mx-auto bg-white border rounded-xl p-4 mb-6 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Google Sheet Connection
            </h2>
            {googleStatus.loading ? (
              <p className="text-sm text-gray-500 mt-1">
                Checking connection status...
              </p>
            ) : googleStatus.error ? (
              <p className="text-sm text-red-600 mt-1">{googleStatus.error}</p>
            ) : googleStatus.data?.connected ? (
              <div className="text-sm text-gray-700 mt-1 space-y-1">
                <p>
                  Connected Email:{" "}
                  <span className="font-medium">
                    {googleStatus.data.ownerEmail || "Not available"}
                  </span>
                </p>
                <p>
                  Sheet Name:{" "}
                  <span className="font-medium">
                    {googleStatus.data.sheetName || "Not available"}
                  </span>
                </p>
                {connectedSheetUrl ? (
                  <a
                    href={connectedSheetUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-600 underline"
                  >
                    Open Connected Sheet
                  </a>
                ) : null}
                <div>
                  <span
                    className={`inline-block px-2 py-1 rounded text-xs font-medium ${statusBadgeClass}`}
                  >
                    Token: {googleStatus.data.tokenStatus}
                  </span>
                </div>
                {googleStatus.data.sheetError ? (
                  <p className="text-xs text-amber-700">
                    {googleStatus.data.sheetError}
                  </p>
                ) : null}
              </div>
            ) : (
              <p className="text-sm text-gray-600 mt-1">
                Google Sheet not connected yet.
              </p>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => redirectToGoogleConnect({ createNew: false })}
              disabled={connectingSheet}
              className="px-3 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-60"
            >
              {googleStatus.data?.connected ? "Reconnect Sheet" : "Connect Sheet"}
            </button>

            <button
              onClick={() => {
                setNewSheetName("");
                setShowCreateSheetModal(true);
              }}
              disabled={connectingSheet}
              className="px-3 py-2 bg-indigo-600 text-white rounded-lg disabled:opacity-60"
            >
              Create New Sheet
            </button>

            <button
              onClick={handleRefreshGoogleStatus}
              className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg border"
            >
              Refresh Status
            </button>

            {googleStatus.data?.needsReconnect ? (
              <button
                onClick={handleRefreshToken}
                disabled={refreshingToken}
                className="px-3 py-2 bg-orange-500 text-white rounded-lg disabled:opacity-60"
              >
                {refreshingToken ? "Refreshing..." : "Refresh Token"}
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <div className="overflow-x-auto w-10/12 mx-auto shadow-lg rounded-2xl">
        <table className="w-full text-left border-collapse">
          <thead className="bg-gray-800 text-white">
            <tr>
              <th className="py-3 px-4">#</th>
              <th className="py-3 px-4">Name</th>
              <th className="py-3 px-4">Email</th>
              <th className="py-3 px-4">Role</th>
              <th className="py-3 px-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {userList.length > 0 ? (
              userList.map((user, index) => (
                <tr
                  key={user.id || index}
                  className={`${index % 2 === 0 ? "bg-gray-50" : "bg-white"} hover:bg-gray-100 transition`}
                >
                  <td className="py-3 px-4 text-gray-600">{index + 1}</td>
                  <td className="py-3 px-4 font-medium text-gray-800">
                    {editingUser === user.id ? (
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) =>
                          setFormData({ ...formData, name: e.target.value })
                        }
                        className="border rounded px-2 py-1 w-full"
                      />
                    ) : (
                      user.name
                    )}
                  </td>
                  <td className="py-3 px-4 text-gray-600">
                    {editingUser === user.id ? (
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) =>
                          setFormData({ ...formData, email: e.target.value })
                        }
                        className="border rounded px-2 py-1 w-full"
                      />
                    ) : (
                      user.email
                    )}
                  </td>
                  <td>
                    {editingUser === user.id ? (
                      <select
                        value={formData.role}
                        onChange={(e) =>
                          setFormData({ ...formData, role: e.target.value })
                        }
                        className="border rounded px-2 py-1"
                      >
                        <option value="">Select Role</option>
                        <option value="admin">Admin</option>
                        <option value="sub-admin">Sub Admin</option>
                        <option value="user">User</option>
                      </select>
                    ) : (
                      <span
                        className={`inline-block px-3 py-1 text-sm rounded-full ${user.role === "admin"
                          ? "bg-blue-100 text-blue-600"
                          : "bg-green-100 text-green-600"
                          }`}
                      >
                        {user.role}
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4 flex gap-2">
                    {editingUser === user.id ? (
                      <>
                        <input
                          type="text"
                          placeholder="PIN"
                          value={formData.pin}
                          onChange={(e) =>
                            setFormData({ ...formData, pin: e.target.value })
                          }
                          className="border rounded px-2 py-1 w-20"
                        />
                        <button
                          onClick={handleSave}
                          className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditingUser(null)}
                          className="px-3 py-1 bg-gray-400 text-white rounded hover:bg-gray-500"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => handleEditClick(user)}
                          className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(user.id)}
                          className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="text-center py-6 text-gray-500">
                  No users found
                </td>
              </tr>
            )}

            {addingUser && (
              <tr className="bg-yellow-50">
                <td className="py-3 px-4">#</td>
                <td className="py-3 px-4">
                  <input
                    type="text"
                    placeholder="Name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="border rounded px-2 py-1 w-full"
                  />
                </td>
                <td className="py-3 px-4">
                  <input
                    type="email"
                    placeholder="Email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    className="border rounded px-2 py-1 w-full"
                  />
                </td>
                <td>
                  <select
                    value={formData.role}
                    onChange={(e) =>
                      setFormData({ ...formData, role: e.target.value })
                    }
                    className="border rounded px-2 py-1"
                  >
                    <option value="">Select Role</option>
                    <option value="admin">Admin</option>
                    <option value="sub-admin">Sub Admin</option>
                    <option value="user">User</option>
                  </select>
                </td>
                <td className="py-3 px-4 flex gap-2">
                  <input
                    type="text"
                    placeholder="PIN"
                    value={formData.pin}
                    onChange={(e) =>
                      setFormData({ ...formData, pin: e.target.value })
                    }
                    className="border rounded px-2 py-1 w-20"
                  />
                  <button
                    onClick={handleAddUser}
                    className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => setAddingUser(false)}
                    className="px-3 py-1 bg-gray-400 text-white rounded hover:bg-gray-500"
                  >
                    Cancel
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {!addingUser && (
          <div className="text-center py-4">
            <button
              onClick={() => {
                setAddingUser(true);
                setFormData(initialUserForm);
              }}
              className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600"
            >
              + Add User
            </button>
          </div>
        )}
      </div>

      {showCreateSheetModal ? (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5">
            <h3 className="text-lg font-semibold mb-2">Create New Google Sheet</h3>
            <p className="text-sm text-gray-600 mb-3">
              Enter sheet name. We will create a new spreadsheet and connect it.
            </p>
            <input
              type="text"
              value={newSheetName}
              onChange={(e) => setNewSheetName(e.target.value)}
              placeholder="e.g. CPHI Leads 2026"
              className="w-full border rounded-lg px-3 py-2 mb-4"
            />
            <div className="flex justify-end gap-2">
              <button
                className="px-3 py-2 rounded-lg border"
                onClick={() => {
                  setShowCreateSheetModal(false);
                  setNewSheetName("");
                }}
              >
                Cancel
              </button>
              <button
                className="px-3 py-2 rounded-lg bg-indigo-600 text-white"
                onClick={handleCreateSheetSubmit}
                disabled={connectingSheet}
              >
                {connectingSheet ? "Redirecting..." : "Create & Connect"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Users;
